import React from "react";
import PropTypes from "prop-types";
import { fmtEur, fmtEurSigned } from "../blocks/format";
import { buildRows as buildDataQualityRows } from "../blocks/dataQualityRows";

// figureDataPanels.jsx - the bespoke, read-only "Data behind this" panels for the board-report
// inspector (BoardReportInspector.jsx). Each panel takes the ALREADY-UNWRAPPED figure object (past
// the writeJSON envelope key) that the matching print block fetches, and renders the numbers behind
// its chart formatted the same way the printed block formats them - reusing blocks/format.js so the
// drawer and the page never disagree. This replaces the old GenericFigureList raw-key dump that
// rendered arrays as "points: 12 items" and floats at full precision.
//
// Panels are keyed by the block's `dataEndpoint` (blockInspector.js's CHART_BLOCK_ENDPOINTS). An
// endpoint with no entry here falls back to the catalog SourceDataPanel / GenericFigureList in
// ChartDataPanel - so kpis/performance/forecast-band (already readable via METRIC_CATALOG) are
// intentionally absent.

// toneClass - a signed euro delta/variance where positive is adverse (over budget / spend went up)
// and negative is favorable (under / a saving), matching the print blocks' varianceTone/impactTone.
// text-danger / text-success are the on-screen tone tokens (tailwind.config.js); neutral stays "".
function toneClass(v) {
  if (typeof v !== "number" || v === 0) return "";
  return v > 0 ? "text-danger" : "text-success";
}

// humanizeCode - snake_case status/confidence codes -> a sentence-case label ("on_track" -> "On
// track", "at_risk" -> "At risk", "high" -> "High", "not_yet_rated" -> "Not yet rated").
function humanizeCode(code) {
  if (!code) return "—";
  const s = String(code).split("_").filter(Boolean).join(" ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// CATEGORY_LABELS / categoryLabel - mirrors DriverTableBlock.jsx's per-driver Volume/Rate copy so
// the drawer's driver list agrees with the printed driver table; unknown codes title-case gracefully.
const CATEGORY_LABELS = { volume: "Volume", rate: "Rate" };
function categoryLabel(category) {
  if (!category) return "—";
  if (CATEGORY_LABELS[category]) return CATEGORY_LABELS[category];
  return category
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function alignClass(align) {
  return align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
}

// KeyValueRows - the compact label/value list (mirrors BoardReportInspector's SourceDataPanel look:
// divide-y rows, muted label, tabular-nums value) for the 2-column panels (risk bridge, data quality).
const KeyValueRows = ({ rows }) => (
  <dl className="divide-y divide-border">
    {rows.map((row) => (
      <div
        key={row.id}
        className={`flex items-baseline justify-between gap-3 py-2 text-xs first:pt-0 last:pb-0 ${
          row.muted ? "text-text-3" : ""
        }`}
      >
        <dt className={`${row.strong ? "font-semibold text-text" : "text-text-2"} ${row.indent ? "pl-3" : ""}`}>
          {row.label}
        </dt>
        <dd className={`tabular-nums ${row.strong ? "font-semibold text-text" : "font-medium text-text"} ${row.valueClass || ""}`}>
          {row.value}
        </dd>
      </div>
    ))}
  </dl>
);

KeyValueRows.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.node,
      value: PropTypes.node,
      strong: PropTypes.bool,
      muted: PropTypes.bool,
      indent: PropTypes.bool,
      valueClass: PropTypes.string,
    })
  ).isRequired,
};

// MiniTable - the compact multi-column table for the list-shaped panels (trend, pvm drivers,
// horizon, detailed cost). Width is set inline (style width:100%) rather than via `w-full`, which
// is a no-op under this project's overridden tailwind width scale (see CLAUDE.md reference). Wraps
// in overflow-x-auto so a wide row scrolls inside the drawer rather than overflowing the page.
const MiniTable = ({ columns, rows }) => (
  <div className="overflow-x-auto">
    <table className="text-xs" style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr className="text-[11px] uppercase tracking-wide text-text-3">
          {columns.map((col) => (
            <th key={col.key} className={`font-medium pb-1.5 pr-3 last:pr-0 ${alignClass(col.align)}`}>
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((row) => (
          <tr key={row._id} className={row._muted ? "text-text-3" : ""}>
            {columns.map((col) => (
              <td
                key={col.key}
                className={`py-1.5 pr-3 last:pr-0 align-baseline ${alignClass(col.align)} ${
                  col.tabular ? "tabular-nums" : ""
                } ${col.cellClass ? col.cellClass(row) : ""}`}
              >
                {col.render ? col.render(row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

MiniTable.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
};

function EmptyNote({ children }) {
  return <p className="text-xs text-text-3">{children}</p>;
}
EmptyNote.propTypes = { children: PropTypes.node };

// ---- Per-endpoint panels -------------------------------------------------------------------

// TrendPanel - figures/trend { points: [{month, cloud, ai, budget, is_forecast}] }. Forecast
// months render muted with a "· forecast" tag so a reader can tell projected from settled spend.
const TrendPanel = ({ figure }) => {
  const points = Array.isArray(figure?.points) ? figure.points : [];
  if (points.length === 0) return <EmptyNote>No trend data.</EmptyNote>;
  const rows = points.map((p, i) => ({ ...p, _id: `${p.month ?? i}`, _muted: !!p.is_forecast }));
  const columns = [
    {
      key: "month",
      header: "Month",
      render: (r) => (
        <span>
          {r.month}
          {r.is_forecast && <span className="text-text-3"> · forecast</span>}
        </span>
      ),
    },
    { key: "cloud", header: "Cloud", align: "right", tabular: true, render: (r) => fmtEur(r.cloud) },
    { key: "ai", header: "AI", align: "right", tabular: true, render: (r) => fmtEur(r.ai) },
    { key: "budget", header: "Budget", align: "right", tabular: true, render: (r) => fmtEur(r.budget) },
  ];
  return <MiniTable columns={columns} rows={rows} />;
};

// PvmPanel - figures/pvm { prev_label, prev_total, current_label, current_total,
// drivers: [{driver, impact|delta, category}] }. Shows the prev->current totals then the FULL
// per-driver breakdown (fixing the old catalog panel that surfaced only the single top driver).
const PvmPanel = ({ figure }) => {
  const drivers = Array.isArray(figure?.drivers) ? figure.drivers : [];
  const totalRows = [];
  if (figure?.prev_label != null || figure?.prev_total != null) {
    totalRows.push({ id: "prev", label: figure.prev_label || "Previous", value: fmtEur(figure.prev_total) });
  }
  if (figure?.current_label != null || figure?.current_total != null) {
    totalRows.push({
      id: "current",
      label: figure.current_label || "Current",
      value: fmtEur(figure.current_total),
      strong: true,
    });
  }
  const rows = drivers.map((d, i) => {
    const impact = typeof d.impact === "number" ? d.impact : d.delta;
    return { _id: d.driver || `driver-${i}`, driver: d.driver || "—", impact, category: d.category };
  });
  const columns = [
    { key: "driver", header: "Driver" },
    {
      key: "impact",
      header: "Impact",
      align: "right",
      tabular: true,
      cellClass: (r) => toneClass(r.impact),
      render: (r) => fmtEurSigned(r.impact),
    },
    { key: "category", header: "Category", render: (r) => categoryLabel(r.category) },
  ];
  return (
    <div className="space-y-3">
      {totalRows.length > 0 && <KeyValueRows rows={totalRows} />}
      {rows.length === 0 ? <EmptyNote>No drivers for this period.</EmptyNote> : <MiniTable columns={columns} rows={rows} />}
    </div>
  );
};

// HorizonPanel - figures/horizon { rows: [{horizon, forecast, budget, variance, confidence,
// status}] }. Same columns as HorizonTableBlock; variance toned (over budget = adverse).
const HorizonPanel = ({ figure }) => {
  const src = Array.isArray(figure?.rows) ? figure.rows : [];
  if (src.length === 0) return <EmptyNote>No horizon data.</EmptyNote>;
  const rows = src.map((r, i) => ({ ...r, _id: `${r.horizon ?? i}` }));
  const columns = [
    { key: "horizon", header: "Horizon", render: (r) => `${r.horizon} days` },
    { key: "forecast", header: "Forecast", align: "right", tabular: true, render: (r) => fmtEur(r.forecast) },
    { key: "budget", header: "Budget", align: "right", tabular: true, render: (r) => fmtEur(r.budget) },
    {
      key: "variance",
      header: "Variance",
      align: "right",
      tabular: true,
      cellClass: (r) => toneClass(r.variance),
      render: (r) => fmtEurSigned(r.variance),
    },
    { key: "confidence", header: "Confidence", render: (r) => humanizeCode(r.confidence) },
    { key: "status", header: "Status", render: (r) => humanizeCode(r.status) },
  ];
  return <MiniTable columns={columns} rows={rows} />;
};

// RiskBridgePanel - figures/risk-bridge { base_label, base_total, legs: [{label, kind, delta}],
// upper_label, upper_total, band_upper }. Renders the bridge as base -> each leg -> risk-adjusted
// upper, replacing the raw "legs: 3 items / band_upper: 110.27..." dump from the screenshot.
const RiskBridgePanel = ({ figure }) => {
  if (!figure || typeof figure !== "object") return <EmptyNote>No risk-bridge data.</EmptyNote>;
  const legs = Array.isArray(figure.legs) ? figure.legs : [];
  const rows = [];
  rows.push({ id: "base", label: figure.base_label || "Base forecast", value: fmtEur(figure.base_total) });
  legs.forEach((leg, i) => {
    rows.push({
      id: `leg-${i}`,
      label: leg.label || `Risk ${i + 1}`,
      value: fmtEurSigned(leg.delta),
      indent: true,
      valueClass: toneClass(leg.delta),
    });
  });
  rows.push({ id: "upper", label: figure.upper_label || "Risk-adjusted upper", value: fmtEur(figure.upper_total), strong: true });
  if (figure.band_upper != null) {
    rows.push({ id: "band", label: "Forecast band upper", value: fmtEur(figure.band_upper), muted: true });
  }
  return <KeyValueRows rows={rows} />;
};

// DataQualityPanel - figures/data-quality. Reuses DataQualityBlock's exported buildRows so the
// drawer shows the exact same Control/Result rows (e.g. "Estimated rather than invoiced cost" ->
// "n/a") as the printed control-summary table, instead of the raw estimated_pct/allocation_
// coverage_pct floats from the screenshot.
const DataQualityPanel = ({ figure }) => {
  if (!figure || typeof figure !== "object") return <EmptyNote>No data-quality figures.</EmptyNote>;
  const built = buildDataQualityRows(figure);
  const rows = built.map((r) => ({ id: r.id, label: r.control, value: r.result }));
  return <KeyValueRows rows={rows} />;
};

// DetailedCostPanel - figures/detailed-cost { rows: [{group, cloud, ai, total, budget, variance}] }.
// Same rendering rules as DetailedCostBlock: budget 0 -> "—" (no per-group budget), and variance is
// only meaningful against a real budget, else neutral "—".
const DetailedCostPanel = ({ figure }) => {
  const src = Array.isArray(figure?.rows) ? figure.rows : [];
  if (src.length === 0) return <EmptyNote>No detailed cost data.</EmptyNote>;
  const rows = src.map((r, i) => ({ ...r, _id: `${r.group ?? i}` }));
  const columns = [
    { key: "group", header: "Group" },
    { key: "cloud", header: "Cloud", align: "right", tabular: true, render: (r) => fmtEur(r.cloud) },
    { key: "ai", header: "AI", align: "right", tabular: true, render: (r) => fmtEur(r.ai) },
    { key: "total", header: "Total", align: "right", tabular: true, render: (r) => fmtEur(r.total) },
    { key: "budget", header: "Budget", align: "right", tabular: true, render: (r) => (r.budget > 0 ? fmtEur(r.budget) : "—") },
    {
      key: "variance",
      header: "Variance",
      align: "right",
      tabular: true,
      cellClass: (r) => (r.budget > 0 ? toneClass(r.variance) : ""),
      render: (r) => (r.budget > 0 ? fmtEurSigned(r.variance) : "—"),
    },
  ];
  return <MiniTable columns={columns} rows={rows} />;
};

// ForecastMonthlyPanel - figures/forecast-monthly { rows: [{month, label, expected, best, worst,
// budget}] }. Same columns as ForecastMonthlyTableBlock; money is float euros (fmtEur direct).
const ForecastMonthlyPanel = ({ figure }) => {
  const src = Array.isArray(figure?.rows) ? figure.rows : [];
  if (src.length === 0) return <EmptyNote>No forward months in the forecast range.</EmptyNote>;
  const rows = src.map((r, i) => ({ ...r, _id: `${r.month ?? i}` }));
  const columns = [
    { key: "label", header: "Month" },
    { key: "expected", header: "Expected", align: "right", tabular: true, render: (r) => fmtEur(r.expected) },
    { key: "best", header: "Best", align: "right", tabular: true, render: (r) => fmtEur(r.best) },
    { key: "worst", header: "Worst", align: "right", tabular: true, render: (r) => fmtEur(r.worst) },
    { key: "budget", header: "Budget", align: "right", tabular: true, render: (r) => fmtEur(r.budget) },
  ];
  return <MiniTable columns={columns} rows={rows} />;
};

// --- Budget Variance & Risk panels --------------------------------------------------------
// These four figures carry money as integer CENTS (the print blocks divide by 100 before
// fmtEur), so the panels do the same via eurCents/eurCentsSigned to stay penny-for-penny
// consistent with the printed tables.
const eurCents = (c) => fmtEur((c ?? 0) / 100);
const eurCentsSigned = (c) => fmtEurSigned((c ?? 0) / 100);
const pctLabel = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(1)}%`);
const daysLabel = (n) => (n == null ? "—" : `${n} day${n === 1 ? "" : "s"}`);

// BudgetRiskOverviewPanel - figures/budget-risk-overview { kpis, atRisk: [...], timeline: [...] }.
// Mirrors BudgetRiskOverviewBlock: the KPI row as key/values, then the at-risk table (or a note).
const BudgetRiskOverviewPanel = ({ figure }) => {
  if (!figure || typeof figure !== "object") return <EmptyNote>No budget-risk data.</EmptyNote>;
  const kpis = figure.kpis || null;
  const atRisk = Array.isArray(figure.atRisk) ? figure.atRisk : [];
  const kpiRows = kpis
    ? [
        { id: "atRisk", label: "At risk", value: `${kpis.atRisk} / ${kpis.activeTotal}`, valueClass: kpis.atRisk > 0 ? "text-danger" : "" },
        { id: "exposure", label: "Overage exposure", value: eurCents(kpis.overageExposureCents), valueClass: kpis.overageExposureCents > 0 ? "text-danger" : "" },
        {
          id: "nearest",
          label: "Nearest breach",
          value: kpis.nearestBreachDays != null ? `${daysLabel(kpis.nearestBreachDays)} · ${kpis.nearestBreachBudget}` : "—",
        },
        { id: "portfolio", label: "Portfolio projected / cap", value: `${eurCents(kpis.portfolioProjectedCents)} / ${eurCents(kpis.portfolioCapCents)}` },
      ]
    : [];
  const rows = atRisk.map((r, i) => ({ ...r, _id: `${r.budgetId ?? i}` }));
  const columns = [
    { key: "name", header: "Budget" },
    { key: "owner", header: "Owner", render: (r) => r.owner || "—" },
    { key: "capCents", header: "Cap", align: "right", tabular: true, render: (r) => eurCents(r.capCents) },
    { key: "projectedCents", header: "Projected", align: "right", tabular: true, render: (r) => eurCents(r.projectedCents) },
    { key: "overByCents", header: "Over by", align: "right", tabular: true, cellClass: () => "text-danger", render: (r) => eurCentsSigned(r.overByCents) },
    { key: "daysToBreach", header: "Days to breach", align: "right", tabular: true, render: (r) => daysLabel(r.daysToBreach) },
    { key: "severity", header: "Severity", align: "center", render: (r) => humanizeCode(r.severity) },
  ];
  return (
    <div className="space-y-3">
      {kpiRows.length > 0 && <KeyValueRows rows={kpiRows} />}
      {rows.length === 0 ? <EmptyNote>No budgets are currently projected to breach their caps.</EmptyNote> : <MiniTable columns={columns} rows={rows} />}
    </div>
  );
};

// BudgetVariancePanel - figures/budget-variance { rows: [...], total: {name:"Portfolio", ...} }.
// Same columns as BudgetVarianceTableBlock, with the portfolio total appended as a strong row.
const BudgetVariancePanel = ({ figure }) => {
  const src = Array.isArray(figure?.rows) ? figure.rows : [];
  const total = figure?.total || null;
  if (src.length === 0 && !total) return <EmptyNote>No variance data.</EmptyNote>;
  const rows = src.map((r, i) => ({ ...r, _id: `${r.budgetId ?? i}` }));
  if (total) rows.push({ ...total, _id: "__total__", _muted: true });
  const columns = [
    { key: "name", header: "Budget" },
    { key: "owner", header: "Owner", render: (r) => r.owner || "—" },
    { key: "capCents", header: "Cap", align: "right", tabular: true, render: (r) => eurCents(r.capCents) },
    { key: "actualCents", header: "Actual", align: "right", tabular: true, render: (r) => eurCents(r.actualCents) },
    { key: "forecastCents", header: "Forecast", align: "right", tabular: true, render: (r) => eurCents(r.forecastCents) },
    { key: "varianceCents", header: "Variance", align: "right", tabular: true, cellClass: (r) => toneClass(r.varianceCents), render: (r) => eurCentsSigned(r.varianceCents) },
    { key: "variancePct", header: "Var %", align: "right", tabular: true, render: (r) => pctLabel(r.variancePct) },
    { key: "status", header: "Status", render: (r) => (r.status ? humanizeCode(r.status) : "—") },
  ];
  return <MiniTable columns={columns} rows={rows} />;
};

// BudgetOwnershipPanel - figures/budget-ownership { owners: [...] }. Same columns as
// BudgetOwnershipTableBlock's accountability-by-team table.
const BudgetOwnershipPanel = ({ figure }) => {
  const src = Array.isArray(figure?.owners) ? figure.owners : [];
  if (src.length === 0) return <EmptyNote>No ownership data.</EmptyNote>;
  const rows = src.map((r, i) => ({ ...r, _id: `${r.owner ?? i}` }));
  const columns = [
    { key: "owner", header: "Owner", render: (r) => r.owner || "—" },
    { key: "budgets", header: "Budgets", align: "right", tabular: true },
    { key: "capCents", header: "Cap", align: "right", tabular: true, render: (r) => eurCents(r.capCents) },
    { key: "projectedCents", header: "Projected", align: "right", tabular: true, render: (r) => eurCents(r.projectedCents) },
    { key: "atRiskCount", header: "At risk", align: "right", tabular: true, cellClass: (r) => (r.atRiskCount > 0 ? "text-danger" : ""), render: (r) => r.atRiskCount },
    { key: "onTrackCount", header: "On track", align: "right", tabular: true },
  ];
  return <MiniTable columns={columns} rows={rows} />;
};

// PreventiveActionsPanel - figures/preventive-actions { actions: [...], summary: {...} }. The actions
// table plus a summary line (exposure addressed) matching PreventiveActionsBlock's badge.
const PreventiveActionsPanel = ({ figure }) => {
  const src = Array.isArray(figure?.actions) ? figure.actions : [];
  const summary = figure?.summary || null;
  const summaryRows = summary
    ? [
        { id: "addressed", label: "Exposure addressed", value: `${eurCents(summary.addressedCents)} of ${eurCents(summary.exposureCents)}` },
        { id: "counts", label: "Tracked · in progress", value: `${summary.total} · ${summary.inProgress}` },
      ]
    : [];
  const rows = src.map((r, i) => ({ ...r, _id: `${r.id ?? i}` }));
  const columns = [
    { key: "title", header: "Action" },
    { key: "budgetName", header: "Budget", render: (r) => r.budgetName || "Portfolio-wide" },
    { key: "owner", header: "Owner", render: (r) => r.owner || "—" },
    { key: "targetDate", header: "Target", render: (r) => r.targetDate || "—" },
    { key: "status", header: "Status", render: (r) => humanizeCode(r.status) },
    { key: "expectedSavingCents", header: "Saving", align: "right", tabular: true, render: (r) => (r.expectedSavingCents != null ? eurCents(r.expectedSavingCents) : "—") },
    { key: "expectedBreachDeferDays", header: "Defer", align: "right", tabular: true, render: (r) => daysLabel(r.expectedBreachDeferDays) },
  ];
  return (
    <div className="space-y-3">
      {summaryRows.length > 0 && <KeyValueRows rows={summaryRows} />}
      {rows.length === 0 ? <EmptyNote>No preventive actions are being tracked yet.</EmptyNote> : <MiniTable columns={columns} rows={rows} />}
    </div>
  );
};

// --- Optimization & Savings panels --------------------------------------------------------
// These four figures carry money as FLOAT euros (unlike the budget-risk figures above), so the
// panels use fmtEur directly - no /100 scaling.

// SavingsSummaryPanel - figures/savings-summary { kpis: {committedTotal, realizedTotal,
// pipelineTotal, realizationRate (0..1), counts: {potential, committed, realized, dismissed}} }.
const SavingsSummaryPanel = ({ figure }) => {
  const kpis = figure?.kpis || null;
  if (!kpis) return <EmptyNote>No savings summary data.</EmptyNote>;
  const counts = kpis.counts || {};
  const rows = [
    { id: "committed", label: "Committed", value: fmtEur(kpis.committedTotal) },
    { id: "realized", label: "Realized", value: fmtEur(kpis.realizedTotal) },
    { id: "pipeline", label: "Pipeline", value: fmtEur(kpis.pipelineTotal) },
    { id: "rate", label: "Realization rate", value: `${Math.round((kpis.realizationRate || 0) * 100)}%` },
    {
      id: "counts",
      label: "Counts (P/C/R/D)",
      value: `${counts.potential ?? 0} / ${counts.committed ?? 0} / ${counts.realized ?? 0} / ${counts.dismissed ?? 0}`,
    },
  ];
  return <KeyValueRows rows={rows} />;
};

// ImplementedActionsPanel - figures/implemented-actions { rows: [{id, title, owner, committed,
// realized, estimatedRealized?, targetDate, status}] }.
const ImplementedActionsPanel = ({ figure }) => {
  const src = Array.isArray(figure?.rows) ? figure.rows : [];
  if (src.length === 0) return <EmptyNote>No implemented savings actions yet.</EmptyNote>;
  const rows = src.map((r, i) => ({ ...r, _id: `${r.id ?? i}` }));
  const columns = [
    { key: "title", header: "Title" },
    { key: "owner", header: "Owner", render: (r) => r.owner || "—" },
    { key: "committed", header: "Committed", align: "right", tabular: true, render: (r) => fmtEur(r.committed) },
    { key: "realized", header: "Realized", align: "right", tabular: true, render: (r) => fmtEur(r.realized) },
    {
      key: "estimatedRealized",
      header: "Verified",
      align: "right",
      tabular: true,
      render: (r) => (r.estimatedRealized != null ? fmtEur(r.estimatedRealized) : "—"),
    },
    { key: "status", header: "Status", render: (r) => humanizeCode(r.status) },
  ];
  return <MiniTable columns={columns} rows={rows} />;
};

// OpenOpportunitiesPanel - figures/open-opportunities { rows: [{id, title, owner,
// potentialSaving, targetDate}], total: {potentialSaving} }. Total appended as a muted row.
const OpenOpportunitiesPanel = ({ figure }) => {
  const src = Array.isArray(figure?.rows) ? figure.rows : [];
  const total = figure?.total || null;
  if (src.length === 0 && !total) return <EmptyNote>No open savings opportunities.</EmptyNote>;
  const rows = src.map((r, i) => ({ ...r, _id: `${r.id ?? i}` }));
  if (total) rows.push({ _id: "__total__", _muted: true, title: "Total", potentialSaving: total.potentialSaving });
  const columns = [
    { key: "title", header: "Title" },
    { key: "owner", header: "Owner", render: (r) => r.owner || "—" },
    { key: "potentialSaving", header: "Potential", align: "right", tabular: true, render: (r) => fmtEur(r.potentialSaving) },
    { key: "targetDate", header: "Target", render: (r) => r.targetDate || "—" },
  ];
  return <MiniTable columns={columns} rows={rows} />;
};

// SavingsVerificationPanel - figures/savings-verification { rows: [{id, title, committed,
// realized, estimatedRealized?, status, targetDate}], summary }.
const SavingsVerificationPanel = ({ figure }) => {
  const src = Array.isArray(figure?.rows) ? figure.rows : [];
  if (src.length === 0) return <EmptyNote>No savings verification data.</EmptyNote>;
  const rows = src.map((r, i) => ({ ...r, _id: `${r.id ?? i}` }));
  const columns = [
    { key: "title", header: "Title" },
    { key: "committed", header: "Committed", align: "right", tabular: true, render: (r) => fmtEur(r.committed) },
    { key: "realized", header: "Realized", align: "right", tabular: true, render: (r) => fmtEur(r.realized) },
    {
      key: "estimatedRealized",
      header: "Verified",
      align: "right",
      tabular: true,
      render: (r) => (r.estimatedRealized != null ? fmtEur(r.estimatedRealized) : "—"),
    },
    { key: "status", header: "Status", render: (r) => humanizeCode(r.status) },
  ];
  return <MiniTable columns={columns} rows={rows} />;
};

const PANEL_PROP_TYPES = { figure: PropTypes.object };
TrendPanel.propTypes = PANEL_PROP_TYPES;
PvmPanel.propTypes = PANEL_PROP_TYPES;
HorizonPanel.propTypes = PANEL_PROP_TYPES;
RiskBridgePanel.propTypes = PANEL_PROP_TYPES;
DataQualityPanel.propTypes = PANEL_PROP_TYPES;
DetailedCostPanel.propTypes = PANEL_PROP_TYPES;
ForecastMonthlyPanel.propTypes = PANEL_PROP_TYPES;
BudgetRiskOverviewPanel.propTypes = PANEL_PROP_TYPES;
BudgetVariancePanel.propTypes = PANEL_PROP_TYPES;
BudgetOwnershipPanel.propTypes = PANEL_PROP_TYPES;
PreventiveActionsPanel.propTypes = PANEL_PROP_TYPES;
SavingsSummaryPanel.propTypes = PANEL_PROP_TYPES;
ImplementedActionsPanel.propTypes = PANEL_PROP_TYPES;
OpenOpportunitiesPanel.propTypes = PANEL_PROP_TYPES;
SavingsVerificationPanel.propTypes = PANEL_PROP_TYPES;

// FIGURE_DATA_PANELS - endpoint -> panel component. Exported so ChartDataPanel can both look up a
// panel and check `hasFigurePanel(endpoint)` to decide bespoke-vs-catalog rendering.
export const FIGURE_DATA_PANELS = {
  "figures/trend": TrendPanel,
  "figures/pvm": PvmPanel,
  "figures/horizon": HorizonPanel,
  "figures/risk-bridge": RiskBridgePanel,
  "figures/data-quality": DataQualityPanel,
  "figures/detailed-cost": DetailedCostPanel,
  "figures/forecast-monthly": ForecastMonthlyPanel,
  "figures/budget-risk-overview": BudgetRiskOverviewPanel,
  "figures/budget-variance": BudgetVariancePanel,
  "figures/budget-ownership": BudgetOwnershipPanel,
  "figures/preventive-actions": PreventiveActionsPanel,
  "figures/savings-summary": SavingsSummaryPanel,
  "figures/implemented-actions": ImplementedActionsPanel,
  "figures/open-opportunities": OpenOpportunitiesPanel,
  "figures/savings-verification": SavingsVerificationPanel,
};

export function hasFigurePanel(endpoint) {
  return Object.prototype.hasOwnProperty.call(FIGURE_DATA_PANELS, endpoint);
}

/**
 * FigureDataPanel - dispatches an endpoint + its unwrapped figure object to the matching bespoke
 * panel. Returns null when no panel is registered for the endpoint (the caller then falls back to
 * the catalog SourceDataPanel / GenericFigureList).
 */
const FigureDataPanel = ({ endpoint, figure }) => {
  const Panel = FIGURE_DATA_PANELS[endpoint];
  if (!Panel) return null;
  return <Panel figure={figure} />;
};

FigureDataPanel.propTypes = {
  endpoint: PropTypes.string,
  figure: PropTypes.object,
};

export default FigureDataPanel;
